import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useGame } from '../../contexts/GameContext'
import UserDisplay from '../UserDisplay/UserDisplay'
import './Leaderboard.css'

export default function Leaderboard({ onViewProfile }) {
  const { user } = useAuth()
  const { getLeaderboard } = useGame()
  const [leaderboard, setLeaderboard] = useState([])
  const [originalLeaderboard, setOriginalLeaderboard] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sortBy, setSortBy] = useState('rating') // 'rating', 'games_won', 'win_rate', 'earnings'
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredLeaderboard, setFilteredLeaderboard] = useState([])

  useEffect(() => {
    loadLeaderboard()
  }, [])

  useEffect(() => {
    if (originalLeaderboard.length > 0) {
      sortLeaderboard(sortBy)
    }
  }, [sortBy, originalLeaderboard])

  useEffect(() => {
    if (leaderboard.length > 0) {
      const filtered = leaderboard.filter(player =>
        player.username.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredLeaderboard(filtered)
      setCurrentPage(1) // Reset to first page when searching
    }
  }, [searchTerm, leaderboard])

  const loadLeaderboard = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getLeaderboard(100)
      // Add win_rate calculation and earnings if not present, filter players with at least 10 games
      const processedData = data
        .map(player => ({
          ...player,
          win_rate: player.games_played > 0 ? (player.games_won / player.games_played) * 100 : 0,
          earnings: player.earnings || 0 // Add earnings field if not present
        }))
        .filter(player => player.games_played >= 3) // Only show players with 3+ games
      setOriginalLeaderboard(processedData)
      setLeaderboard(processedData)
    } catch (err) {
      console.error('Failed to load leaderboard:', err)
      setError('Failed to load leaderboard data')
    } finally {
      setLoading(false)
    }
  }

  const sortLeaderboard = (criteria) => {
    const sorted = [...originalLeaderboard].sort((a, b) => {
      switch (criteria) {
        case 'rating':
          return b.rating - a.rating
        case 'games_won':
          return b.games_won - a.games_won
        case 'win_rate':
          return b.win_rate - a.win_rate
        case 'earnings':
          return b.earnings - a.earnings
        default:
          return b.rating - a.rating
      }
    })

    // Reassign ranks based on the new sort order
    const rankedData = sorted.map((player, index) => ({
      ...player,
      rank: index + 1
    }))

    setLeaderboard(rankedData)
    setCurrentPage(1) // Reset to first page when sorting changes
  }

  // Pagination calculations
  const displayData = filteredLeaderboard.length > 0 ? filteredLeaderboard : leaderboard
  const totalPages = Math.ceil(displayData.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentItems = displayData.slice(startIndex, endIndex)

  // Helper function to get dynamic value based on sort criteria
  const getDynamicValue = (player, criteria) => {
    switch (criteria) {
      case 'rating':
        return player.rating
      case 'games_won':
        return player.games_won
      case 'win_rate':
        return Math.round(player.win_rate) + '%'
      case 'earnings':
        return '$' + (player.earnings || 0)
      default:
        return player.rating
    }
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    // Scroll to top of leaderboard when page changes
    document.querySelector('.leaderboard-content')?.scrollIntoView({ behavior: 'smooth' })
  }

  const getRankClass = (rank) => {
    if (rank === 1) return 'rank-gold'
    if (rank === 2) return 'rank-silver'
    if (rank === 3) return 'rank-bronze'
    if (rank <= 10) return 'rank-top10'
    return 'rank-normal'
  }

  const getRankIcon = (rank, isTable = false) => {
    if (isTable) {
      // For table, just show the rank number without squares
      return `#${rank}`
    }
    // For podium, keep the squares
    if (rank === 1) return <div className="rank-square rank-first">1</div>
    if (rank === 2) return <div className="rank-square rank-second">2</div>
    if (rank === 3) return <div className="rank-square rank-third">3</div>
    return `#${rank}`
  }

  if (loading) {
    return (
      <div className="leaderboard-container">
        <div className="leaderboard-header">
          <h1>Leaderboard</h1>
        </div>
        <div className="leaderboard-loading">
          <div className="loading-spinner">Loading leaderboard...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="leaderboard-container">
        <div className="leaderboard-header">
          <h1>Leaderboard</h1>
        </div>
        <div className="leaderboard-error">
          <p>{error}</p>
          <button className="retry-button" onClick={loadLeaderboard}>
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-header">
        <div className="leaderboard-logo">
          <img
            src="/images/logo/finallogosquare.png"
            alt="RiftChess"
            className="logo-icon"
          />
        </div>
        <h1>Leaderboard</h1>
        <p>Top players ranked by {sortBy === 'rating' ? 'ELO rating' : sortBy === 'games_won' ? 'games won' : sortBy === 'win_rate' ? 'win rate' : 'earnings'}</p>
      </div>

      {/* Filter Controls */}
      <div className="leaderboard-filters">
        <div className="filter-section">
          <div className="filter-buttons">
          <button
            className={`filter-btn ${sortBy === 'rating' ? 'active' : ''}`}
            onClick={() => setSortBy('rating')}
          >
            ELO Rating
          </button>
          <button
            className={`filter-btn ${sortBy === 'games_won' ? 'active' : ''}`}
            onClick={() => setSortBy('games_won')}
          >
            Games Won
          </button>
          <button
            className={`filter-btn ${sortBy === 'win_rate' ? 'active' : ''}`}
            onClick={() => setSortBy('win_rate')}
          >
            Win Rate
          </button>
          <button
            className={`filter-btn ${sortBy === 'earnings' ? 'active' : ''}`}
            onClick={() => setSortBy('earnings')}
          >
            Earnings
          </button>
          </div>
        </div>
      </div>

      <div className="leaderboard-content">
        {/* Top 3 Podium */}
        {leaderboard.length >= 3 && (
          <div className="podium-section">
            <div className="podium">
              {/* First place */}
              <div className="podium-place first">
                <div className="podium-player">
                  <div className="podium-rank">{getRankIcon(1)}</div>
                  <div className="podium-name" onClick={() => onViewProfile?.(leaderboard[0].username)}>
                    <UserDisplay user={leaderboard[0]} showTitleBadges={true} showStatusBadges={true} context="leaderboard" />
                  </div>
                  <div className={`podium-rating ${sortBy === 'rating' ? 'highlighted' : ''}`}>
                    {getDynamicValue(leaderboard[0], sortBy)}
                  </div>
                </div>
                <div className="podium-bar first-bar">1st</div>
              </div>

              {/* Second place */}
              <div className="podium-place second">
                <div className="podium-player">
                  <div className="podium-rank">{getRankIcon(2)}</div>
                  <div className="podium-name" onClick={() => onViewProfile?.(leaderboard[1].username)}>
                    <UserDisplay user={leaderboard[1]} showTitleBadges={true} showStatusBadges={true} context="leaderboard" />
                  </div>
                  <div className={`podium-rating ${sortBy === 'rating' ? 'highlighted' : ''}`}>
                    {getDynamicValue(leaderboard[1], sortBy)}
                  </div>
                </div>
                <div className="podium-bar second-bar">2nd</div>
              </div>

              {/* Third place */}
              <div className="podium-place third">
                <div className="podium-player">
                  <div className="podium-rank">{getRankIcon(3)}</div>
                  <div className="podium-name" onClick={() => onViewProfile?.(leaderboard[2].username)}>
                    <UserDisplay user={leaderboard[2]} showTitleBadges={true} showStatusBadges={true} context="leaderboard" />
                  </div>
                  <div className={`podium-rating ${sortBy === 'rating' ? 'highlighted' : ''}`}>
                    {getDynamicValue(leaderboard[2], sortBy)}
                  </div>
                </div>
                <div className="podium-bar third-bar">3rd</div>
              </div>
            </div>
          </div>
        )}

        {/* Full Rankings Table */}
        <div className="rankings-section">
          <div className="rankings-header-row">
            <h2>Full Rankings</h2>
            <div className="search-section">
              <input
                type="text"
                placeholder="Search players..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
          <div className="rankings-table">
            <div className="rankings-header">
              <div className="rank-col">Rank</div>
              <div className="player-col">Player</div>
              <div className="rating-col">Rating</div>
              <div className="games-col">Games Won</div>
              <div className="total-games-col">Total Games</div>
              <div className="winrate-col">Win Rate</div>
              <div className="earnings-col">Earnings</div>
            </div>

            <div className="rankings-body">
              {currentItems.map((player, index) => {
                const isCurrentUser = player.id === user?.id
                return (
                  <div
                    key={player.id}
                    className={`ranking-row ${getRankClass(player.rank)} ${isCurrentUser ? 'current-user' : ''}`}
                  >
                    <div className="rank-col">
                      <span className="rank-display">{getRankIcon(player.rank, true)}</span>
                    </div>
                    <div className="player-col">
                      <div className="player-info">
                        <span className="player-name" onClick={() => onViewProfile?.(player.username)}>
                          <UserDisplay user={player} showTitleBadges={true} showStatusBadges={true} context="leaderboard" />
                          {isCurrentUser && <span className="you-badge">YOU</span>}
                        </span>
                      </div>
                    </div>
                    <div className="rating-col">
                      <span className="rating-value">{player.rating}</span>
                    </div>
                    <div className="games-col">
                      <span className="games-value">{player.games_won}</span>
                    </div>
                    <div className="total-games-col">
                      <span className="total-games-value">{player.games_played}</span>
                    </div>
                    <div className="winrate-col">
                      <span className="winrate-value">{Math.round(player.win_rate)}%</span>
                    </div>
                    <div className="earnings-col">
                      <span className="earnings-value">${player.earnings || 0}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {leaderboard.length === 0 && (
            <div className="no-players">
              <p>No ranked players yet. Play at least 3 games to appear on the leaderboard!</p>
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                Showing {startIndex + 1}-{Math.min(endIndex, leaderboard.length)} of {leaderboard.length} players
              </div>
              <div className="pagination-buttons">
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let page;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={page}
                      className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}